import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  UserPlus, CreditCard, FileText, Share2, Users, Mail, Video, ArrowRight,
  CheckCircle, Globe, Eye, Wallet, BarChart3, Heart,
} from 'lucide-react';
import { usePageMeta } from '@/hooks/usePageMeta';
import PageShell, {
  PageHero, LightSection, DarkSection, SectionHead, FeatureCard,
  ClosingBanner, PrimaryCTA, SecondaryCTA,
} from '@/components/PageShell';

const HOW_IT_WORKS_DESCRIPTION =
  "How Sport Fund works in four steps: sign up, build your profile, connect your bank, and share your fund page. Start receiving support in minutes.";

export default function HowItWorks() {
  const { t } = useTranslation();

  // Order mirrors the real in-app checklist (SetupChecklist.tsx: photo -> bio -> first
  // post -> connect bank -> publish -> share), not the reverse — the bank/KYC step used
  // to be listed second, right after signup, which overstates how soon it's needed and
  // reads as a payments ask before a creator has built anything. Confirmed via PostHog
  // (2026-08-26) that a chatgpt.com creator-intent visitor read this page in that order
  // and dropped before reaching /signup-creator.
  const steps = [
    { icon: UserPlus, title: t('howItWorksPage.workflow.steps.signup.title'), body: t('howItWorksPage.workflow.steps.signup.description') },
    { icon: FileText, title: t('howItWorksPage.workflow.steps.profile.title'), body: t('howItWorksPage.workflow.steps.profile.description') },
    { icon: CreditCard, title: t('howItWorksPage.workflow.steps.payments.title'), body: t('howItWorksPage.workflow.steps.payments.description') },
    { icon: Share2, title: t('howItWorksPage.workflow.steps.share.title'), body: t('howItWorksPage.workflow.steps.share.description') },
    { icon: Mail, title: t('howItWorksPage.workflow.steps.updates.title'), body: t('howItWorksPage.workflow.steps.updates.description') },
  ];

  // HowTo structured data — helps Google + answer engines describe the athlete onboarding flow.
  const howToJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: 'How to raise funds for your sport with Sport Fund',
    description: HOW_IT_WORKS_DESCRIPTION,
    step: steps.map((s, i) => ({
      '@type': 'HowToStep',
      position: i + 1,
      name: s.title,
      text: s.body,
    })),
  };

  usePageMeta('How It Works', HOW_IT_WORKS_DESCRIPTION, {
    jsonLd: howToJsonLd,
    breadcrumbs: [{ name: 'How It Works' }],
  });

  const athleteFeatures = [
    { icon: <FileText className="h-6 w-6" />, title: t('howItWorksPage.creatorFeatures.customProfile.title'), body: t('howItWorksPage.creatorFeatures.customProfile.description') },
    { icon: <Wallet className="h-6 w-6" />, title: t('howItWorksPage.creatorFeatures.directPayments.title'), body: t('howItWorksPage.creatorFeatures.directPayments.description') },
    { icon: <Video className="h-6 w-6" />, title: t('howItWorksPage.creatorFeatures.contentSharing.title'), body: t('howItWorksPage.creatorFeatures.contentSharing.description') },
    { icon: <Users className="h-6 w-6" />, title: t('howItWorksPage.creatorFeatures.audienceManagement.title'), body: t('howItWorksPage.creatorFeatures.audienceManagement.description') },
    { icon: <Mail className="h-6 w-6" />, title: t('howItWorksPage.creatorFeatures.emailIntegration.title'), body: t('howItWorksPage.creatorFeatures.emailIntegration.description') },
    { icon: <BarChart3 className="h-6 w-6" />, title: t('howItWorksPage.creatorFeatures.analytics.title'), body: t('howItWorksPage.creatorFeatures.analytics.description') },
  ];

  const supporterFeatures = [
    { icon: <Heart className="h-6 w-6" />, title: t('howItWorksPage.donorFeatures.easyDonations.title'), body: t('howItWorksPage.donorFeatures.easyDonations.description') },
    { icon: <Eye className="h-6 w-6" />, title: t('howItWorksPage.donorFeatures.followUpdates.title'), body: t('howItWorksPage.donorFeatures.followUpdates.description') },
    { icon: <CheckCircle className="h-6 w-6" />, title: t('howItWorksPage.donorFeatures.prayerRequests.title'), body: t('howItWorksPage.donorFeatures.prayerRequests.description') },
    { icon: <Globe className="h-6 w-6" />, title: t('howItWorksPage.donorFeatures.transparency.title'), body: t('howItWorksPage.donorFeatures.transparency.description') },
  ];

  return (
    <PageShell
      hero={
        <PageHero
          eyebrow="How it works"
          title={
            <>
              From idea to first contribution in{' '}
              <span className="bg-gradient-to-r from-yellow-300 via-yellow-400 to-amber-300 bg-clip-text text-transparent">two minutes.</span>
            </>
          }
          lead={t('howItWorksPage.hero.subtitle')}
          actions={
            <>
              <Link to="/signup-creator">
                <PrimaryCTA>
                  Start your fund page <ArrowRight className="h-5 w-5" />
                </PrimaryCTA>
              </Link>
              <Link to="/fund/demo">
                <SecondaryCTA>Tour a demo</SecondaryCTA>
              </Link>
            </>
          }
        />
      }
    >
      <LightSection>
        <SectionHead
          eyebrow="The flow"
          title={t('howItWorksPage.workflow.title')}
          lead="Each step is built so an athlete with no design or technical background can move through it in a single sitting."
        />
        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-5 gap-5">
          {steps.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={i} className="relative rounded-3xl bg-gradient-to-br from-slate-50 to-white border border-slate-200/70 p-6 hover:border-slate-300 hover:-translate-y-1 transition-all duration-300 shadow-sm hover:shadow-lg">
                <div className="text-6xl font-black tracking-tighter text-slate-100 absolute -top-1 right-2 pointer-events-none select-none" aria-hidden>
                  {String(i + 1).padStart(2, '0')}
                </div>
                <div className="relative">
                  <div className="h-11 w-11 rounded-xl bg-blue-50 text-blue-700 grid place-items-center">
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-5 text-lg font-bold text-slate-900 tracking-tight">{s.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.body}</p>
                </div>
              </div>
            );
          })}
        </div>
      </LightSection>

      <LightSection bg="gray">
        <SectionHead
          eyebrow="For athletes"
          title={t('howItWorksPage.creatorFeatures.title')}
          lead={t('howItWorksPage.creatorFeatures.subtitle')}
        />
        <div className="mt-14 grid md:grid-cols-3 gap-5">
          {athleteFeatures.map((f, i) => (
            <FeatureCard key={i} icon={f.icon} title={f.title} body={f.body} />
          ))}
        </div>
      </LightSection>

      <DarkSection>
        <SectionHead
          tone="dark"
          eyebrow="For supporters"
          title={t('howItWorksPage.donorFeatures.title')}
          lead={t('howItWorksPage.donorFeatures.subtitle')}
        />
        <div className="mt-14 grid md:grid-cols-2 gap-5">
          {supporterFeatures.map((f, i) => (
            <FeatureCard key={i} tone="dark" icon={f.icon} title={f.title} body={f.body} />
          ))}
        </div>
      </DarkSection>

      <ClosingBanner
        eyebrow="Live in two minutes"
        title={
          <>
            Your fund page can be{' '}
            <span className="bg-gradient-to-r from-yellow-300 via-yellow-400 to-amber-300 bg-clip-text text-transparent">live tonight.</span>
          </>
        }
        lead={t('howItWorksPage.cta.description')}
        primary={
          <Link to="/signup-creator">
            <PrimaryCTA>{t('howItWorksPage.cta.button')} <ArrowRight className="h-5 w-5" /></PrimaryCTA>
          </Link>
        }
        secondary={
          <Link to="/faqs">
            <SecondaryCTA>Common questions</SecondaryCTA>
          </Link>
        }
      />
    </PageShell>
  );
}
